import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import { TopProgressBar } from './components/TopProgressBar';

// Context for Auth
const AuthContext = createContext(null);

// Context for Theme
const ThemeContext = createContext(null);

// Context for Platform Landing
const PlatformContext = createContext(null);

export const useAuth = () => useContext(AuthContext);
export const useTheme = () => useContext(ThemeContext);
export const usePlatform = () => useContext(PlatformContext);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// API instance with auth
export const apiClient = axios.create({
  baseURL: API,
  headers: { 'Content-Type': 'application/json' }
});

// ── Global slow-load progress bar ──────────────────────────────
// Only requests that stay in-flight past SLOW_MS flip the bar on, so cached
// (fast) calls never flash it while cold first-time computes show feedback.
let _pActive = 0;
const SLOW_MS = 400;
const _emitProgress = () => {
  try { window.dispatchEvent(new CustomEvent('api-progress', { detail: _pActive })); }
  catch { /* SSR / no window */ }
};
function _finishProgress(config) {
  if (!config) return;
  if (config._slowTimer) { clearTimeout(config._slowTimer); config._slowTimer = null; }
  if (config._slow) { config._slow = false; _pActive = Math.max(0, _pActive - 1); _emitProgress(); }
}

// ── Per-navigation request cancellation ────────────────────────
// Every request inherits a shared AbortSignal for the current "epoch".
// When the user switches tabs we abort the epoch — cancelling any request
// still in-flight from the tab they just left — and start a fresh one. This
// stops a slow/hanging request (e.g. during an Atlas blip) from keeping the
// global progress bar spinning or firing the gateway toast on an unrelated
// tab. Canceled requests are swallowed silently in the response interceptor.
let _reqController = new AbortController();
export function abortInFlightRequests() {
  try { _reqController.abort(); } catch { /* noop */ }
  _reqController = new AbortController();
  _pActive = 0; _emitProgress();
}

// Add auth interceptor
apiClient.interceptors.request.use((config) => {
  // Prefer sessionStorage (per-tab embed sessions) over localStorage
  // (per-browser logged-in user). This lets an operator embed multiple
  // PIs in separate tabs without their JWTs clobbering each other.
  const token = sessionStorage.getItem('token') || localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config._slowTimer = setTimeout(() => {
    config._slow = true; _pActive += 1; _emitProgress();
  }, SLOW_MS);
  if (!config.signal && (config.method || 'get').toLowerCase() === 'get') {
    config.signal = _reqController.signal;
  }
  return config;
});

// Add response interceptor to handle auth errors + friendly gateway errors.
// Cloudflare fronts the deployed URL. When a cold-cache endpoint takes
// longer than ~100s CF returns a 5xx (520-527, typically 524) with its own
// HTML error page. Users saw the raw Cloudflare error page which is noisy
// and confusing. We intercept those status codes here, plus generic network
// errors / timeouts, and rewrite the error into a friendly message so any
// component that awaits an apiClient call can render (or toast) a clean
// explanation with a "prime the cache" nudge instead of the CF HTML.
const CLOUDFLARE_GATEWAY_STATUSES = new Set([520, 521, 522, 523, 524, 525, 526, 527, 502, 503, 504]);
const FRIENDLY_GATEWAY_MESSAGE =
  "This report is being prepared for the first time — it'll be ready in a moment. " +
  "Please try again shortly; subsequent loads will be instant. If it keeps happening, ask an admin to prime the cache for this PI on the Data Update Status page.";

// ───────────────────────────────────────────────────────────────
// Silent access-token renewal.
//
// On any 401 from a non-auth call, we transparently:
//   1. hit `POST /auth/refresh` with the stored refresh_token + device_id,
//   2. persist the freshly rotated tokens,
//   3. retry the original request exactly once with the new access token.
//
// Concurrency: multiple in-flight requests that all 401 at once must not
// each fire a refresh — we serialise them behind a single shared promise.
// Retry-loop guard: each request carries `_retriedAfterRefresh=true` after
// its first retry, so a second 401 does NOT trigger another refresh (and
// we fall through to the wipe-and-redirect path).
// The refresh call uses a bare axios (no interceptors) to avoid recursion.
// ───────────────────────────────────────────────────────────────
let _refreshInFlight = null;
// Throttle the "Session refreshed" toast so a burst of concurrent 401s
// (or a series of natural refreshes over the workday) doesn't spam.
let _lastRefreshToastAt = 0;
const REFRESH_TOAST_THROTTLE_MS = 15 * 60 * 1000;  // at most once every 15 min

async function _runSilentRefresh() {
  if (_refreshInFlight) return _refreshInFlight;
  _refreshInFlight = (async () => {
    const rt  = localStorage.getItem('refresh_token');
    const dev = localStorage.getItem('device_id');
    if (!rt) throw new Error('no_refresh_token');
    let resp;
    try {
      resp = await axios.post(`${API}/auth/refresh`,
        { refresh_token: rt, device_id: dev },
        { headers: { 'Content-Type': 'application/json' } });
    } catch (err) {
      // Categorize the failure so the login page can toast the right reason.
      const detail = err?.response?.data?.detail || '';
      let reason = 'session_expired';
      if (/already used|revoked|reuse/i.test(detail))     reason = 'session_revoked';
      else if (/different device|device_mismatch/i.test(detail)) reason = 'session_device_mismatch';
      else if (/expired/i.test(detail))                    reason = 'session_expired';
      sessionStorage.setItem('logout_reason', reason);
      throw err;
    }
    const { access_token, refresh_token: new_rt } = resp.data || {};
    if (!access_token) throw new Error('bad_refresh_response');
    localStorage.setItem('token', access_token);
    if (new_rt) localStorage.setItem('refresh_token', new_rt);
    // Subtle "Session refreshed" toast — throttled, and only when the
    // user actually has something to see (not the very first hydration).
    try {
      const now = Date.now();
      if (now - _lastRefreshToastAt > REFRESH_TOAST_THROTTLE_MS) {
        _lastRefreshToastAt = now;
        toast('Session refreshed', {
          description: 'Your login was silently renewed — no re-auth needed.',
          duration: 3000,
          id: 'session-refreshed',
        });
      }
    } catch { /* toast is best-effort */ }
    return access_token;
  })().finally(() => { _refreshInFlight = null; });
  return _refreshInFlight;
}

// Exported for raw fetch() callers (e.g. the SSE chat stream) that bypass the
// axios interceptor but still need the silent 401 refresh-and-retry behaviour.
export const refreshAuthToken = () => _runSilentRefresh();

function _wipeAndBootToLogin(reason) {
  // Preserve any reason already set by _runSilentRefresh; otherwise use the
  // caller-supplied reason (default: session_expired).
  if (reason && !sessionStorage.getItem('logout_reason')) {
    sessionStorage.setItem('logout_reason', reason);
  }
  localStorage.removeItem('token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('user');
  sessionStorage.removeItem('idle_locked');
  localStorage.removeItem('last_activity_ts');
  if (!window.location.pathname.includes('/login')) {
    window.location.href = '/';
  }
}

apiClient.interceptors.response.use(
  (response) => { _finishProgress(response.config); return response; },
  async (error) => {
    _finishProgress(error.config);
    // Requests cancelled on tab switch (abortInFlightRequests) settle here —
    // swallow them silently: no gateway toast, no sign-out, no retry.
    if (axios.isCancel?.(error) || error.code === 'ERR_CANCELED' || error.name === 'CanceledError' || error.message === 'canceled') {
      return Promise.reject(error);
    }
    const status  = error?.response?.status;
    const cfg     = error.config || {};
    const url     = cfg.url || '';
    const isAuthCall = /\/(auth\/login|auth\/refresh|auth\/unlock|auth\/change-password)/.test(url);

    // ── 401 handling ────────────────────────────────────────────
    // 401 = token invalid/expired.  Try silent refresh once; on
    // failure, boot to login.  403 (authenticated but forbidden)
    // is unrelated — surface it to the caller unchanged.
    if (status === 401 && !isAuthCall) {
      const hasRefresh = !!localStorage.getItem('refresh_token');
      if (hasRefresh && !cfg._retriedAfterRefresh) {
        try {
          const newToken = await _runSilentRefresh();
          cfg._retriedAfterRefresh = true;
          cfg.headers = { ...(cfg.headers || {}), Authorization: `Bearer ${newToken}` };
          return apiClient(cfg);
        } catch (_refreshErr) {
          // Refresh itself failed — the reason was already captured in
          // sessionStorage by _runSilentRefresh; boot to login.
          _wipeAndBootToLogin();
          return Promise.reject(error);
        }
      }
      // No refresh token, or we've already retried once — hard sign-out.
      _wipeAndBootToLogin('session_expired');
    }

    // ── Cloudflare / network friendly errors (unchanged) ───────
    const isCfGateway = status && CLOUDFLARE_GATEWAY_STATUSES.has(status);
    const isNetworkOrTimeout = !error.response && (error.code === 'ECONNABORTED' || error.message === 'Network Error' || /timeout/i.test(error?.message || ''));
    if (isCfGateway || isNetworkOrTimeout) {
      const friendly = new Error(FRIENDLY_GATEWAY_MESSAGE);
      friendly.isGatewayTimeout = true;
      friendly.originalStatus   = status || null;
      friendly.originalMessage  = error.message;
      friendly.response         = { status: status || 0, data: { detail: FRIENDLY_GATEWAY_MESSAGE, gatewayTimeout: true } };
      try {
        if (!window.__gatewayToastAt || Date.now() - window.__gatewayToastAt > 8000) {
          window.__gatewayToastAt = Date.now();
          toast.warning(FRIENDLY_GATEWAY_MESSAGE, { duration: 8000, id: 'gateway-timeout' });
        }
      } catch { /* toast is best-effort */ }
      return Promise.reject(friendly);
    }
    return Promise.reject(error);
  }
);

// Theme Provider
const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // Embed launches (`/ehilive?t=<token>`) should always start on the
    // light theme — the embedding platform may itself be light and we
    // don't want to inherit an unrelated saved preference.
    try {
      const path = window.location.pathname || '';
      const hasEmbedToken = new URLSearchParams(window.location.search).get('t');
      if (path.startsWith('/ehilive') && hasEmbedToken) return 'light';
    } catch { /* noop */ }
    // First check platform theme (from landing page), then regular theme
    const platformTheme = localStorage.getItem('platformTheme');
    if (platformTheme) {
      // Convert platform themes to light/dark
      return platformTheme === 'light' ? 'light' : 'dark';
    }
    const saved = localStorage.getItem('theme');
    return saved || 'dark';
  });

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Helper to check if JWT token is expired on client side
  const isTokenExpired = (token) => {
    if (!token) return true;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      // Check if token has expired (with 5 minute buffer)
      return payload.exp * 1000 < Date.now() - 5 * 60 * 1000;
    } catch {
      return true;
    }
  };

  useEffect(() => {
    // Validate token on app load
    const validateToken = async () => {
      const token = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');
      
      if (!token || !savedUser) {
        // No token or user - clear everything and require login
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setLoading(false);
        return;
      }
      
      // Client-side expiration check first (faster).
      // If the access token is expired BUT we still have a refresh_token,
      // let the request proceed anyway — the axios interceptor will
      // silently rotate the token and retry.  This is what preserves the
      // session across long tab idle periods (up to the 30-day refresh TTL).
      if (isTokenExpired(token) && !localStorage.getItem('refresh_token')) {
        console.warn('Token expired (client-side check) and no refresh_token — requiring re-login');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        setLoading(false);
        return;
      }
      
      try {
        // Verify token is still valid by calling a protected endpoint
        const response = await apiClient.get('/auth/me');
        if (response.data && response.data.username) {
          // Safety net for backend/frontend version drift: an older
          // backend may respond to /auth/me with a `User` model that
          // pre-dates the ACL fields.  Pydantic silently strips those
          // fields from the response, which would clobber the ACL
          // flags we already have from /auth/login and break the
          // ProtectedRoute guard.  Preserve any ACL field that /auth/me
          // did NOT return but that we already had from login.
          const meData = response.data;
          const prevUser = (() => { try { return JSON.parse(savedUser) || {}; } catch { return {}; } })();
          const ACL_KEYS = [
            'access_ehilive',
            'access_revqinsights',
            'access_admin_users',
            'access_data_update_status',
            'access_revqinsights_config',
            'access_postingautoapply',
            'postingautoapply_pi_global',
            'postingautoapply_pi_scope',
          ];
          const merged = { ...meData };
          for (const k of ACL_KEYS) {
            if (!(k in meData) && (k in prevUser)) {
              merged[k] = prevUser[k];
            }
          }
          setUser(merged);
          localStorage.setItem('user', JSON.stringify(merged));
        } else {
          throw new Error('Invalid response');
        }
      } catch (error) {
        // Token is invalid/expired - clear and require re-login
        console.warn('Token validation failed, requiring re-login');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    
    validateToken();
  }, []);

  // Stable per-browser device id — used to bind the refresh-token family
  // to this device (theft detection).  Persists across tabs.
  const getDeviceId = () => {
    let d = localStorage.getItem('device_id');
    if (!d) {
      d = (window.crypto?.randomUUID?.() ||
           `dev-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
      localStorage.setItem('device_id', d);
    }
    return d;
  };

  const login = async (username, password) => {
    const device_id = getDeviceId();
    const response = await apiClient.post('/auth/login', { username, password, device_id });
    const { access_token, refresh_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    if (refresh_token) localStorage.setItem('refresh_token', refresh_token);
    localStorage.setItem('user', JSON.stringify(userData));
    // Reset idle-lock + logout-reason state so a fresh login always
    // starts UNLOCKED and doesn't inherit a stale toast reason.
    sessionStorage.removeItem('idle_locked');
    sessionStorage.removeItem('logout_reason');
    localStorage.setItem('last_activity_ts', String(Date.now()));
    setUser(userData);
    return userData;
  };

  const markPasswordChanged = () => {
    setUser(prev => {
      if (!prev) return prev;
      const next = { ...prev, must_change_password: false };
      localStorage.setItem('user', JSON.stringify(next));
      return next;
    });
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    sessionStorage.removeItem('idle_locked');
    localStorage.removeItem('last_activity_ts');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, markPasswordChanged }}>
      {children}
    </AuthContext.Provider>
  );
};

// Lazy load pages
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const PlatformLandingPage = React.lazy(() => import('./pages/PlatformLandingPage'));
const AnalyticsStudioPage = React.lazy(() => import('./pages/AnalyticsStudioPage'));
const PublicDashboardPage = React.lazy(() => import('./pages/PublicDashboardPage'));
const EHiBridgeDashboardPage = React.lazy(() => import('./pages/EHiBridgeDashboardPage'));
const EHiLiveDashboardPage = React.lazy(() => import('./pages/EHiLiveDashboardPage'));
const EHiLiveExplorePage = React.lazy(() => import('./pages/EHiLiveExplorePage'));
const EHiLiveFleetPage = React.lazy(() => import('./pages/EHiLiveExplorePage').then((m) => ({ default: m.EHiLiveFleetPage })));
const ClientUIPage = React.lazy(() => import('./pages/ClientUIPage'));
const AdminBuilderPage = React.lazy(() => import('./pages/AdminBuilderPage'));
const ComboAnalysisPage = React.lazy(() => import('./pages/ComboAnalysisPage'));
const ComboAnalysisPageRD = React.lazy(() => import('./pages/ComboAnalysisPageRD'));
const ClientUIV2Page = React.lazy(() => import('./pages/ClientUIV2Page'));
const V2OnlyHomePage = React.lazy(() => import('./pages/V2OnlyHomePage'));
const UserManagementPage = React.lazy(() => import('./pages/UserManagementPage'));
const DataUpdateStatusPage = React.lazy(() => import('./pages/DataUpdateStatusPage'));
const RvuPage = React.lazy(() => import('./pages/RvuPage'));
const PostingPage = React.lazy(() => import('./pages/PostingPage'));
const SwotAnalysisPage = React.lazy(() => import('./pages/SwotAnalysisPage'));
const RevQInsightsPage = React.lazy(() => import('./pages/RevQInsightsPage'));
const RevQInsightsConfigPage = React.lazy(() => import('./pages/RevQInsightsConfigPage'));
const ReportsPage = React.lazy(() => import('./pages/ReportsPage'));
const ProductChooserPage = React.lazy(() => import('./pages/ProductChooserPage'));
const PostingAutoApplyPage = React.lazy(() => import('./pages/PostingAutoApplyPage'));
const ProductionAnalyzerPage = React.lazy(() => import('./pages/ProductionAnalyzerRouter'));
const ClaimLedgerViewPage = React.lazy(() => import('./pages/ClaimLedgerViewPage'));
const RuleReviewPage = React.lazy(() => import('./pages/RuleReviewPage'));
const AriaOneLuminaPage = React.lazy(() => import('./pages/AriaOneLuminaPage'));
const EDIMappingPage = React.lazy(() => import('./pages/EDIMappingPage'));import ForcePasswordResetModal from './components/ForcePasswordResetModal';
const EDIClientsPage = React.lazy(() => import('./pages/EDIClientsPage'));
const ClaimCheckPage = React.lazy(() => import('./pages/ClaimCheckPage'));
import IdleLock from './components/IdleLock';

// Platform Provider - manages landing page visibility
const PlatformProvider = ({ children }) => {
  const [showLanding, setShowLanding] = useState(() => {
    // Check if user has seen landing this session
    const seenThisSession = sessionStorage.getItem('landingSeen');
    return !seenThisSession;
  });
  
  const [defaultToLanding, setDefaultToLanding] = useState(() => {
    const saved = localStorage.getItem('defaultToLanding');
    return saved === 'true';
  });
  
  const dismissLanding = () => {
    sessionStorage.setItem('landingSeen', 'true');
    setShowLanding(false);
  };
  
  const goToLanding = () => {
    setShowLanding(true);
  };
  
  const toggleDefaultLanding = (value) => {
    setDefaultToLanding(value);
    localStorage.setItem('defaultToLanding', value.toString());
  };
  
  return (
    <PlatformContext.Provider value={{ 
      showLanding, 
      dismissLanding, 
      goToLanding,
      defaultToLanding,
      toggleDefaultLanding
    }}>
      {children}
    </PlatformContext.Provider>
  );
};

// Protected Route with Landing Page support
const ProtectedRoute = ({ children, skipLanding = false, v2OnlyAllowed = false, requireAcl = null }) => {
  const { user, loading } = useAuth();
  const { showLanding, dismissLanding } = usePlatform();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }
  
  // Embed / SSO launch bypass — if another platform sent the user to
  // `/ehilive?t=<opaque-token>` we MUST let the EHiLiveDashboardPage
  // mount so it can POST the token to /api/ehilive/embed/resolve and
  // render the locked, single-PI view. Redirecting to /login here would
  // strip the token and break the embed flow entirely.
  const isEhiLiveEmbedLaunch = (() => {
    try {
      const path = window.location.pathname || '';
      if (!path.startsWith('/ehilive')) return false;
      const t = new URLSearchParams(window.location.search).get('t');
      return Boolean(t);
    } catch {
      return false;
    }
  })();

  if (!user) {
    if (isEhiLiveEmbedLaunch) {
      return children;
    }
    return <Navigate to="/login" replace />;
  }

  // Force-password-reset gate: if the backend says the user must change
  // password, block ALL protected routes behind an undismissible modal.
  // Skipped for embed launches — the embed viewer isn't the logged-in user.
  if (user.must_change_password && !isEhiLiveEmbedLaunch) {
    return <ForcePasswordGate user={user} />;
  }

  // v2_only users are restricted to V2 Builder + ClientUI v2 + their own home.
  // Any other protected route bounces them back to "/".
  if (user.role === 'v2_only' && !v2OnlyAllowed) {
    return <Navigate to="/" replace />;
  }

  // Per-user ACL gate — used for admin pages like /admin/users and
  // /data-update-status.  adminrevq is always granted regardless.
  if (requireAcl) {
    const isSeed = String(user.username || '').toLowerCase() === 'adminrevq';
    const hasAcl = isSeed || user[requireAcl] === true;
    if (!hasAcl) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-6">
          <div className="max-w-md text-center space-y-4">
            <div className="text-5xl">🔒</div>
            <div className="text-xl font-semibold">Access restricted</div>
            <div className="text-sm text-muted-foreground">
              You don&apos;t have permission to view this page.
              Ask an admin to enable <span className="font-mono">{requireAcl}</span> for your account.
            </div>
            <button onClick={() => (window.location.href = '/')}
                    className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm">
              Back to home
            </button>
          </div>
        </div>
      );
    }
  }
  
  // Skip the platform landing entirely for v2_only users.
  if (user.role !== 'v2_only' && showLanding && !skipLanding) {
    return (
      <React.Suspense fallback={
        <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f]">
          <div className="animate-pulse text-gray-400">Loading Platform...</div>
        </div>
      }>
        <PlatformLandingPage onContinue={dismissLanding} user={user} />
      </React.Suspense>
    );
  }
  
  return children;
};

// Root route picker: v2_only users see the minimal V2 home; everyone else
// gets the full DashboardPage.
const RootRoute = () => {
  const { user } = useAuth();
  if (user?.role === 'v2_only') {
    return <V2OnlyHomePage />;
  }
  return <DashboardPage />;
};

// Pick the best landing route for a user based on their per-user ACL.
// Users with broad EHi Live access (or adminrevq) keep landing on /ehilive.
// A user scoped to a single module (e.g. only access_postingautoapply) is
// sent straight to that module instead of the Explore landing they can't use.
const homePathForUser = (user) => {
  if (!user) return '/ehilive';
  const canEhi  = user.access_ehilive !== false;
  const canRevQ = user.access_revqinsights !== false;
  const canPaa  = user.access_postingautoapply === true;
  if ([canEhi, canRevQ, canPaa].filter(Boolean).length >= 2) return '/choose-product';
  if (canEhi) return '/ehilive';
  if (canPaa) return '/PostingAutoApply';
  if (canRevQ) return '/revq-insights';
  if (user.access_data_update_status === true) return '/data-update-status';
  if (user.access_revqinsights_config === true) return '/RevQInsightsConfig';
  if (user.access_admin_users === true) return '/admin/users';
  return '/ehilive';
};

// ACL-aware landing redirect used by "/" and the catch-all route.
const HomeRedirect = () => {
  const { user } = useAuth();
  return <Navigate to={homePathForUser(user)} replace />;
};


// Undismissible full-screen gate shown while the user still has
// must_change_password === true. Solving the modal clears the flag
// in-memory (via markPasswordChanged) and the outer ProtectedRoute
// re-renders the real destination.
const ForcePasswordGate = ({ user }) => {
  const { markPasswordChanged, logout } = useAuth();
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <ForcePasswordResetModal
        open={true}
        username={user.username}
        onComplete={markPasswordChanged}
        onLogout={logout}
      />
    </div>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <PlatformProvider>
          <BrowserRouter>
            <TopProgressBar />
            <IdleLock>
              <React.Suspense fallback={
              <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="animate-pulse text-muted-foreground">Loading...</div>
              </div>
            }>
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/choose-product" element={
                  <ProtectedRoute skipLanding>
                    <ProductChooserPage />
                  </ProtectedRoute>
                } />
                <Route path="/platform" element={
                  <ProtectedRoute>
                    <PlatformLandingPage />
                  </ProtectedRoute>
                } />
                <Route path="/studio" element={
                  <ProtectedRoute skipLanding>
                    <AnalyticsStudioPage />
                  </ProtectedRoute>
                } />
                <Route path="/studio/:projectId" element={
                  <ProtectedRoute skipLanding>
                    <AnalyticsStudioPage />
                  </ProtectedRoute>
                } />
                <Route path="/public/dashboard/:projectId" element={
                  <ProtectedRoute skipLanding>
                    <PublicDashboardPage />
                  </ProtectedRoute>
                } />
                <Route path="/ehibridge" element={
                  <ProtectedRoute skipLanding>
                    <EHiBridgeDashboardPage />
                  </ProtectedRoute>
                } />
                <Route path="/ehilive" element={
                  <ProtectedRoute skipLanding>
                    <EHiLiveDashboardPage />
                  </ProtectedRoute>
                } />
                <Route path="/ehilive/explore" element={
                  <ProtectedRoute skipLanding>
                    <EHiLiveExplorePage />
                  </ProtectedRoute>
                } />
                <Route path="/ehilive/fleet" element={
                  <ProtectedRoute skipLanding>
                    <EHiLiveFleetPage />
                  </ProtectedRoute>
                } />
                <Route path="/reports" element={
                  <ProtectedRoute skipLanding>
                    <ReportsPage />
                  </ProtectedRoute>
                } />
                <Route path="/ehilive/combo" element={
                  <ProtectedRoute skipLanding>
                    <ComboAnalysisPage />
                  </ProtectedRoute>
                } />
                <Route path="/datastream" element={
                  <ProtectedRoute skipLanding>
                    <ClaimCheckPage />
                  </ProtectedRoute>
                } />
                <Route path="/datastream/claim-check" element={
                  <ProtectedRoute skipLanding>
                    <ClaimCheckPage />
                  </ProtectedRoute>
                } />
                <Route path="/ehilive/combo_version_rd" element={
                  <ProtectedRoute skipLanding>
                    <ComboAnalysisPageRD />
                  </ProtectedRoute>
                } />
                <Route path="/client-ui" element={
                  <ProtectedRoute skipLanding>
                    <ClientUIPage />
                  </ProtectedRoute>
                } />
                <Route path="/client-ui-v2" element={
                  <ProtectedRoute skipLanding v2OnlyAllowed>
                    <ClientUIV2Page />
                  </ProtectedRoute>
                } />
                <Route path="/admin/client-ui-v2" element={
                  <ProtectedRoute skipLanding v2OnlyAllowed>
                    <AdminBuilderPage />
                  </ProtectedRoute>
                } />
                <Route path="/admin/users" element={
                  <ProtectedRoute skipLanding requireAcl="access_admin_users">
                    <UserManagementPage />
                  </ProtectedRoute>
                } />
                <Route path="/data-update-status" element={
                  <ProtectedRoute skipLanding requireAcl="access_data_update_status">
                    <DataUpdateStatusPage />
                  </ProtectedRoute>
                } />
                <Route path="/EDI" element={
                  <ProtectedRoute skipLanding>
                    <EDIClientsPage />
                  </ProtectedRoute>
                } />
                <Route path="/EDI/:pi" element={
                  <ProtectedRoute skipLanding>
                    <EDIMappingPage />
                  </ProtectedRoute>
                } />
                <Route path="/rvu" element={
                  <ProtectedRoute skipLanding>
                    <RvuPage />
                  </ProtectedRoute>
                } />
                <Route path="/posting" element={
                  <ProtectedRoute skipLanding>
                    <PostingPage />
                  </ProtectedRoute>
                } />
                <Route path="/PostingAutoApply" element={
                  <ProtectedRoute skipLanding requireAcl="access_postingautoapply">
                    <PostingAutoApplyPage />
                  </ProtectedRoute>
                } />
                <Route path="/PostingAutoApply/Production" element={
                  <ProtectedRoute skipLanding requireAcl="access_postingautoapply">
                    <ProductionAnalyzerPage />
                  </ProtectedRoute>
                } />
                <Route path="/PostingAutoApply/ClaimLedger" element={
                  <ProtectedRoute skipLanding requireAcl="access_postingautoapply">
                    <ClaimLedgerViewPage />
                  </ProtectedRoute>
                } />
                <Route path="/PostingAutoApply/Review" element={
                  <ProtectedRoute skipLanding requireAcl="access_postingautoapply">
                    <RuleReviewPage />
                  </ProtectedRoute>
                } />
                <Route path="/PostingAutoApply/Review/AccountInfo" element={
                  <ProtectedRoute skipLanding requireAcl="access_postingautoapply">
                    <RuleReviewPage />
                  </ProtectedRoute>
                } />
                <Route path="/aria-one-lumina" element={
                  <ProtectedRoute skipLanding requireAcl="access_postingautoapply">
                    <AriaOneLuminaPage />
                  </ProtectedRoute>
                } />
                <Route path="/swot" element={
                  <ProtectedRoute skipLanding>
                    <SwotAnalysisPage />
                  </ProtectedRoute>
                } />
                <Route path="/RevQInsights" element={
                  <ProtectedRoute skipLanding>
                    <RevQInsightsPage />
                  </ProtectedRoute>
                } />
                <Route path="/revq-insights" element={
                  <ProtectedRoute skipLanding>
                    <RevQInsightsPage />
                  </ProtectedRoute>
                } />
                <Route path="/revqinsights-config" element={
                  <ProtectedRoute skipLanding requireAcl="access_revqinsights_config">
                    <RevQInsightsConfigPage />
                  </ProtectedRoute>
                } />
                <Route path="/RevQInsightsConfig" element={
                  <ProtectedRoute skipLanding requireAcl="access_revqinsights_config">
                    <RevQInsightsConfigPage />
                  </ProtectedRoute>
                } />
                {/* Root path redirects to the user's best landing route
                    (ACL-aware): EHi Live for broad users, or the single
                    module a scoped user can access (e.g. PostingAutoApply).
                    v2_only users get bounced to their scoped home via the
                    ProtectedRoute guard downstream. */}
                <Route path="/" element={
                  <ProtectedRoute skipLanding v2OnlyAllowed>
                    <HomeRedirect />
                  </ProtectedRoute>
                } />
                <Route path="*" element={<HomeRedirect />} />
              </Routes>
            </React.Suspense>
            </IdleLock>
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </PlatformProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
